/**
 * Support Email Utility
 * Generates mailto links with order and error information for support requests
 */

import { toast } from 'react-toastify';
import { isDevelopment } from '../env/config';

/**
 * Support email address for SmartSend error reporting
 * @constant {string}
 */
export const SUPPORT_EMAIL = 'support@gesture.vip';

/**
 * Support text constant for error messages
 * @constant {string}
 */
export const SUPPORT_TEXT = 'Contact Support';

/**
 * Maximum length for error messages in support emails (to prevent excessive data)
 * @constant {number}
 */
const MAX_ERROR_MESSAGE_LENGTH = 500;

/**
 * Create regex pattern for matching support text or email (case-insensitive)
 * Escapes special regex characters and creates a pattern for matching either support text or email
 * @returns {Object} Object with supportPattern (for splitting) and testPattern (for testing)
 */
export const createSupportPattern = () => {
  // Escape special regex characters in support text and email
  // Note: @ symbol doesn't need escaping in regex
  const escapedText = SUPPORT_TEXT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedEmail = SUPPORT_EMAIL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create pattern that matches support text or email (case-insensitive)
  // Put email first in alternation to prioritize email matching
  // Email pattern: Match the full email address (no word boundaries needed - @ is clear delimiter)
  // Support text pattern: Use word boundaries and require space/end after "Support" to prevent
  // matching when "support" is part of an email address (e.g., "support@gesture.vip")
  // Note: No 'g' flag is intentional for both use cases:
  // - For split(): it splits on all matches regardless of the 'g' flag
  // - For test(): without 'g', the regex is not stateful (no lastIndex issues)
  // Pattern structure: ("support@gesture.vip" | word-boundary "Contact Support" word-boundary not followed by optional whitespace and @)
  // The (?!\s*@) ensures "Contact Support" doesn't match when it is followed by optional whitespace and then @ (email)
  // This handles edge cases like "Contact Support @gesture.vip" where there's whitespace between text and email
  const pattern = new RegExp(`(${escapedEmail}|\\b${escapedText}\\b(?!\\s*@))`, 'i');

  // Pattern for testing individual parts (case-insensitive, anchored)
  // This is used to check if a split part is the support text/email
  // Put email first to prioritize email matching
  const testPattern = new RegExp(`^(${escapedEmail}|\\b${escapedText}\\b)$`, 'i');

  return {
    supportPattern: pattern,
    testPattern: testPattern,
  };
};

/**
 * Sanitize error message for safe inclusion in email
 * - Truncates very long messages
 * - Removes excessive whitespace
 * - Limits potential data exposure
 * @param {string} message - Error message to sanitize
 * @returns {string} Sanitized error message
 */
const sanitizeErrorMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return '';
  }

  // Remove excessive whitespace and normalize
  let sanitized = message.trim().replace(/\s+/g, ' ');

  // Truncate if too long
  if (sanitized.length > MAX_ERROR_MESSAGE_LENGTH) {
    sanitized = sanitized.substring(0, MAX_ERROR_MESSAGE_LENGTH) + '... [truncated]';
  }

  return sanitized;
};

/**
 * Check if an error is address-related
 * @param {string} errorCode - Error code from backend
 * @param {string} errorMessage - Error message from backend
 * @returns {boolean} True if error is address-related
 */
const isAddressRelatedError = (errorCode, errorMessage) => {
  if (!errorCode && !errorMessage) return false;

  const code = String(errorCode || '').toUpperCase().trim();
  const message = String(errorMessage || '').toLowerCase().trim();

  // Check for address-related error codes
  // These are the known error codes from the backend for address-related issues
  const addressErrorCodes = [
    'DELIVERY_ZONE_NOT_ALLOWED',
    'COUNTRY_NOT_ALLOWED',
    'ADDRESS_INVALID',
    'ADDRESS_VALIDATION_ERROR',
  ];

  // Check for exact match or if code contains the error code
  if (addressErrorCodes.some(addrCode => code === addrCode || code.includes(addrCode))) {
    return true;
  }

  // Check for address-related keywords in error message
  // Use more specific patterns to avoid false positives
  const addressKeywords = [
    'delivery zone',
    'not deliverable',
    'delivery area',
    'outside delivery',
    'cannot be delivered',
    'not deliverable to',
    'delivery zone not allowed',
    'country not allowed',
  ];

  return addressKeywords.some(keyword => message.includes(keyword));
};

/**
 * Generate mailto link with order and error information
 * @param {Object} options - Options for generating the email
 * @param {string} options.orderId - Order ID
 * @param {Object} options.orderData - Order data object
 * @param {string} options.errorMessage - Error message from backend
 * @param {string} options.errorCode - Error code from backend
 * @param {string} [options.phoneNumber] - User's phone number
 *   Included automatically for address-related errors (to help support diagnose issues).
 *   For non-address errors, phone number is omitted for privacy and users fill it in manually.
 * @param {Object} options.address - Address object (included automatically for address-related errors, otherwise users fill in manually)
 * @param {string} [options.subject] - Custom email subject (optional)
 * @param {string} [options.context] - Additional context about where the error occurred
 * @returns {string} mailto URL
 */
export const generateSupportEmailLink = ({
  orderId,
  orderData,
  errorMessage,
  errorCode,
  phoneNumber, // Included for address-related errors, omitted for other errors (privacy)
  address,
  subject,
  context,
}) => {
  // Note: phoneNumber is included in the email body for address-related errors
  // (similar to how address information is included) to help support diagnose the issue.
  // For non-address errors, phone number is not included for privacy reasons.

  // Build email subject
  const emailSubject = subject || `SmartSend Error - Order ${orderId || 'Unknown'}`;

  // Build email body with order and error information (ultra-concise format to avoid truncation)
  const bodyParts = [];

  // Helper to truncate long strings
  const truncate = (str, maxLen = 100) => {
    if (!str) return str;
    const s = String(str).trim();
    if (s.length <= maxLen) return s;
    // Ensure maxLen is at least 3 to avoid negative substring index
    const safeMaxLen = Math.max(3, maxLen);
    return s.substring(0, safeMaxLen - 3) + '...';
  };

  // Friendly introduction message
  bodyParts.push('Please help me resolve issues I encountered when submitting my SmartSend details.\n\n');

  // Error information (ultra-concise)
  bodyParts.push('SmartSend Error\n');
  if (errorCode) {
    bodyParts.push(`Code: ${String(errorCode)}\n`);
  }
  if (errorMessage) {
    const sanitizedMessage = sanitizeErrorMessage(String(errorMessage));
    // Truncate error message to 150 chars to save space
    bodyParts.push(`Error: ${truncate(sanitizedMessage, 150)}\n`);
  }
  if (context) {
    bodyParts.push(`Context: ${truncate(String(context), 50)}\n`);
  }

  // Order information (ultra-concise)
  bodyParts.push('\nOrder:\n');
  if (orderId) {
    bodyParts.push(`ID: ${String(orderId)}\n`);
  }
  if (orderData && typeof orderData === 'object') {
    if (orderData.skuId) {
      bodyParts.push(`SKU: ${truncate(String(orderData.skuId), 50)}\n`);
    } else if (orderData.sku) {
      bodyParts.push(`SKU: ${truncate(String(orderData.sku), 50)}\n`);
    }
    if (orderData.productName) {
      bodyParts.push(`Product: ${truncate(String(orderData.productName), 80)}\n`);
    }
  }

  // User information
  // For address-related errors, include address automatically to help support diagnose the issue
  // For other errors, users fill in manually for privacy
  const isAddressError = isAddressRelatedError(errorCode, errorMessage);

  bodyParts.push('\nContact:\n');

  if (isAddressError && address && typeof address === 'object' && !Array.isArray(address)) {
    // Include address for address-related errors (ultra-compact format)

    // Helper to safely get and format address field with truncation
    const formatAddressField = (value, maxLen = 60) => {
      if (value == null || value === '') return null;
      const stringValue = String(value).trim();
      if (stringValue.length === 0) return null;
      return truncate(stringValue, maxLen);
    };

    // Build compact address line
    const addressParts = [];
    const streetAddress = formatAddressField(address.address1, 50);
    if (streetAddress) {
      addressParts.push(streetAddress);
    }
    const addressLine2 = formatAddressField(address.address2, 30);
    if (addressLine2) {
      addressParts.push(addressLine2);
    }
    if (addressParts.length > 0) {
      bodyParts.push(`Addr: ${addressParts.join(', ')}\n`);
    }

    // City, State, ZIP on one compact line
    const city = formatAddressField(address.locality, 30);
    const state = formatAddressField(address.state, 10);
    const zipCode = formatAddressField(address.postcode, 10);
    const cityStateZip = [city, state, zipCode].filter(Boolean).join(', ');
    if (cityStateZip) {
      bodyParts.push(`Location: ${cityStateZip}\n`);
    }

    const country = formatAddressField(address.country, 10);
    if (country) {
      bodyParts.push(`Country: ${country}\n`);
    }

    // Coordinates and timezone on one compact line
    const lat = typeof address.lat === 'number' && Number.isFinite(address.lat)
      ? address.lat
      : null;
    const lng = typeof address.lng === 'number' && Number.isFinite(address.lng)
      ? address.lng
      : null;
    const timezone = formatAddressField(address.timezone, 30);
    const locationInfo = [];
    if (lat != null && lng != null) {
      // Round coordinates to 4 decimal places to save space (~11m precision at the equator)
      locationInfo.push(`${lat.toFixed(4)},${lng.toFixed(4)}`);
    }
    if (timezone) {
      locationInfo.push(timezone);
    }
    if (locationInfo.length > 0) {
      bodyParts.push(`Geo: ${locationInfo.join(' | ')}\n`);
    }

    // Include phone number for address-related errors to help support diagnose the issue
    // (similar to how we include address information)
    const trimmedPhone = phoneNumber?.trim();
    if (trimmedPhone) {
      bodyParts.push(`Phone: ${truncate(trimmedPhone, 20)}\n`);
    } else {
      bodyParts.push('Phone: n/a\n');
    }
  } else {
    // For non-address errors, maintain privacy (ultra-concise)
    bodyParts.push('Please provide: Phone, Address\n');
  }

  const emailBody = bodyParts.join('');

  // Encode subject for URL
  const encodedSubject = encodeURIComponent(emailSubject);

  // Some browsers have URL length limits (typically 2000-8000 characters)
  // Using 2000 for maximum compatibility with older browsers and email clients
  // With the optimized compact format, typical email bodies encode to ~800-1000 chars,
  // so 2000 provides a safe buffer while ensuring broad compatibility
  const MAX_MAILTO_URL_LENGTH = 2000;
  const baseUrl = `mailto:${SUPPORT_EMAIL}?subject=${encodedSubject}&body=`;

  // Truncate body to fit within URL limit
  // IMPORTANT: Truncate the raw body before encoding to avoid cutting encoded sequences
  const truncationMessage = '\n\n[Email body truncated due to length limit]';
  const encodedTruncationMessage = encodeURIComponent(truncationMessage);

  // Calculate max length for encoded body (which will include truncation message)
  // Final URL = baseUrl + encodedBody, so encodedBody.length <= MAX_MAILTO_URL_LENGTH - baseUrl.length
  const maxEncodedBodyLength = Math.max(
    0,
    MAX_MAILTO_URL_LENGTH - baseUrl.length
  );

  // If baseUrl itself is too long, we can't even fit a truncation message
  // In this case, return baseUrl without body (unlikely but handle edge case)
  if (maxEncodedBodyLength <= 0 || baseUrl.length + encodedTruncationMessage.length > MAX_MAILTO_URL_LENGTH) {
    return baseUrl; // Can't even fit truncation message, return base URL only
  }

  // First, try encoding the full body WITHOUT truncation message to see if it fits
  // This is more accurate than estimating, and for most cases (like this one) it will fit
  // Only add truncation message if we actually need to truncate
  let encodedBody = encodeURIComponent(emailBody);

  // If it fits, return immediately without truncation message
  if (encodedBody.length <= maxEncodedBodyLength) {
    return baseUrl + encodedBody;
  }

  // Only truncate if the encoded body is actually too long
  // Estimate: encoding typically expands text by 2-3x (spaces become %20, special chars %XX)
  // Use 2.5x multiplier for more accurate estimation since we've optimized the format
  // Account for truncation message that will be appended to the body
  const availableLengthForBody = maxEncodedBodyLength - encodedTruncationMessage.length;
  const estimatedMaxRawLength = Math.max(0, Math.floor(availableLengthForBody / 2.5));

  // Ensure we don't exceed the actual body length
  let truncatedRawBody = emailBody.substring(0, Math.min(estimatedMaxRawLength, emailBody.length));

  // If there's no room for any of the original body, just use the truncation message
  if (truncatedRawBody.length === 0) {
    return baseUrl + encodedTruncationMessage;
  }

  encodedBody = encodeURIComponent(truncatedRawBody + truncationMessage);

  // If still too long after encoding, compute the required truncation in a single step
  // This is more efficient than iterative reduction and avoids potential browser timeouts
  if (encodedBody.length > maxEncodedBodyLength) {
    // Calculate the ratio of allowed length to current encoded length
    // This ratio approximates how much of the raw body we can keep
    const allowedRatio = maxEncodedBodyLength / encodedBody.length;

    // Scale down the raw body length proportionally, with a small safety margin
    // The safety margin accounts for encoding variations (different chars encode differently)
    const newLength = Math.max(0, Math.floor(truncatedRawBody.length * allowedRatio) - 1);

    // If we've reduced to nothing or less, return just the truncation message
    if (newLength <= 0) {
      return baseUrl + encodedTruncationMessage;
    }

    truncatedRawBody = truncatedRawBody.substring(0, newLength);
    encodedBody = encodeURIComponent(truncatedRawBody + truncationMessage);

    // Final safety check: if still too long (due to encoding variations), do one more small reduction
    // This should be rare, but handles edge cases where the ratio approximation wasn't precise enough
    if (encodedBody.length > maxEncodedBodyLength && truncatedRawBody.length > 0) {
      // Reduce by a small fixed amount as a final adjustment
      const finalReduction = Math.max(1, Math.floor(truncatedRawBody.length * 0.02)); // 2% reduction
      truncatedRawBody = truncatedRawBody.substring(0, truncatedRawBody.length - finalReduction);

      if (truncatedRawBody.length === 0) {
        return baseUrl + encodedTruncationMessage;
      }

      encodedBody = encodeURIComponent(truncatedRawBody + truncationMessage);
    }
  }

  // Final check: if the reduced body still doesn't fit, return base URL with just truncation message
  if (encodedBody.length > maxEncodedBodyLength) {
    encodedBody = encodedTruncationMessage;
  }

  return baseUrl + encodedBody;
};

/**
 * Open support email in user's default email client
 * Supports both old signature (options object) and new signature (mailtoLink string) for backward compatibility
 * @param {Object|string} optionsOrMailtoLink - Options for generating the email (same as generateSupportEmailLink) OR a mailto URL string
 * @returns {Promise<void>} Promise that resolves when email client is opened (or falls back to web methods)
 */
export const openSupportEmail = async (optionsOrMailtoLink) => {
  if (typeof window === 'undefined') {
    console.warn('[SupportEmail] Cannot open email: window is not available');
    return;
  }

  // Determine if we received options object or mailtoLink string
  const isOptionsObject = optionsOrMailtoLink && typeof optionsOrMailtoLink === 'object' && !Array.isArray(optionsOrMailtoLink);
  let mailtoLink = isOptionsObject
    ? generateSupportEmailLink(optionsOrMailtoLink)
    : (optionsOrMailtoLink || `mailto:${SUPPORT_EMAIL}`);

  // Validate mailtoLink
  if (!mailtoLink || typeof mailtoLink !== 'string') {
    console.error('[Support Email] Invalid mailtoLink provided (non-string or empty):', mailtoLink);
    // Fallback to simple mailto
    mailtoLink = `mailto:${SUPPORT_EMAIL}`;
  } else if (!mailtoLink.toLowerCase().startsWith('mailto:')) {
    console.error('[Support Email] Invalid mailtoLink scheme, expected mailto:', mailtoLink);
    // Fallback to simple mailto if scheme is not mailto:
    mailtoLink = `mailto:${SUPPORT_EMAIL}`;
  }

  if (isDevelopment()) {
    console.log('[Support Email] Opening mailto link:', mailtoLink.substring(0, 100) + '...');
  }

  // Check if we're in a React Native WebView
  const isReactNativeWebView = typeof window !== 'undefined' && window.ReactNativeWebView;

  if (isReactNativeWebView) {
    if (isDevelopment()) {
      console.log('[Support Email] Detected React Native WebView, using postMessage with response handling');
    }

    try {
      // Generate unique request ID for this mailto request
      // Use timestamp + random string to ensure uniqueness
      const requestId = `mailto_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      // Create a Promise that resolves when we get a response from native app
      const waitForResponse = new Promise((resolve, reject) => {
        const RESPONSE_TIMEOUT = 2000; // 2 second timeout
        let timeoutId = null;
        let messageHandler = null;
        let isResolved = false;

        const cleanup = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (messageHandler && typeof window !== 'undefined') {
            window.removeEventListener('message', messageHandler);
            messageHandler = null;
          }
        };

        const resolveOnce = (value) => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            resolve(value);
          }
        };

        const rejectOnce = (error) => {
          if (!isResolved) {
            isResolved = true;
            cleanup();
            reject(error);
          }
        };

        // Set up timeout
        timeoutId = setTimeout(() => {
          rejectOnce(new Error('Timeout waiting for native app response'));
        }, RESPONSE_TIMEOUT);

        // Set up message listener
        messageHandler = (event) => {
          try {
            // Handle both string and object data
            let data = null;
            if (typeof event.data === 'string') {
              try {
                data = JSON.parse(event.data);
              } catch (parseError) {
                // Not JSON, ignore this message
                return;
              }
            } else if (event.data && typeof event.data === 'object') {
              data = event.data;
            } else {
              // Not a valid message format, ignore
              return;
            }

            // Check if this is a response to our request
            if (data && data.type === 'open_mailto_response' && data.requestId === requestId) {
              if (data.success) {
                if (isDevelopment()) {
                  console.log('[Support Email] Native app successfully handled mailto link');
                }
                resolveOnce();
              } else {
                const errorMessage = data.error || 'Native app failed to open email client';
                if (isDevelopment()) {
                  console.warn('[Support Email] Native app reported failure:', errorMessage);
                }
                rejectOnce(new Error(errorMessage));
              }
            }
          } catch (error) {
            // Ignore errors in message handler - don't reject the promise
            // as this might be a message for a different handler
            if (isDevelopment()) {
              console.debug('[Support Email] Error processing message:', error);
            }
          }
        };

        // Add event listener
        if (typeof window !== 'undefined') {
          window.addEventListener('message', messageHandler);
        } else {
          // Window is not available, ensure handler is cleared and reject immediately
          messageHandler = null;
          rejectOnce(new Error('Window not available for message listener'));
        }
      });

      // Send message to React Native to handle mailto link
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'open_mailto',
          requestId: requestId, // Include request ID for response matching
          url: mailtoLink,
        })
      );

      if (isDevelopment()) {
        console.log('[Support Email] Sent mailto link to React Native, waiting for response...');
      }

      // Wait for response from native app
      await waitForResponse;

      // Success - native app handled it
      return;
    } catch (error) {
      // Error or timeout - fall through to web methods
      if (isDevelopment()) {
        console.warn('[Support Email] Native app failed or timed out, falling back to web methods:', error.message);
      }
      // Fall through to web methods below
    }
  }

  // Web browser methods
  if (typeof document === 'undefined') {
    console.error('[Support Email] Document not available');
    return;
  }

  let anchorError = null;
  let locationError = null;
  let anchor = null;
  let timeoutId = null;

  try {
    // Method 1: Use temporary anchor element (preferred for SPAs)
    // This avoids triggering beforeunload handlers and SPA navigation issues
    // while still reliably opening the user's email client
    anchor = document.createElement('a');
    anchor.href = mailtoLink;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();

    // Remove anchor after a short delay
    // The delay ensures the click event is fully processed before cleanup
    timeoutId = setTimeout(() => {
      if (anchor && document.body && document.body.contains(anchor)) {
        try {
          document.body.removeChild(anchor);
        } catch (removeError) {
          // Ignore errors during cleanup - element may have been removed already
        }
      }
    }, 100);

    if (isDevelopment()) {
      console.log('[Support Email] Opened via anchor element');
    }
    return;
  } catch (error) {
    anchorError = error;
    // Clean up anchor element if it was added to DOM before the error
    if (anchor && document.body && document.body.contains(anchor)) {
      try {
        document.body.removeChild(anchor);
      } catch (removeError) {
        // Ignore errors during cleanup
      }
    }
    // Clear timeout if it was set
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (isDevelopment()) {
      console.warn('[Support Email] Anchor method failed, trying window.location:', anchorError);
    }

    // Method 2: Use window.location (fallback)
    try {
      window.location.href = mailtoLink;
      if (isDevelopment()) {
        console.log('[Support Email] Opened via window.location.href');
      }
      return;
    } catch (error) {
      locationError = error;
      if (isDevelopment()) {
        console.warn('[Support Email] window.location.href also failed:', locationError);
      }
    }
  }

  // All methods failed - show fallback
  console.error('[Support Email] Failed to open email client:', anchorError || locationError);

  // Provide user feedback and a fallback when the email client cannot be opened
  // Best-effort attempt to copy the support email address to the clipboard
  try {
    if (navigator?.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(SUPPORT_EMAIL).catch(() => {
        // Ignore clipboard errors; the toast below still gives the address
      });
    }
  } catch (clipboardError) {
    console.warn(
      '[SupportEmail] Failed to copy support email to clipboard:',
      clipboardError
    );
  }

  // Show toast notification with support email address
  // Use info toast with longer duration to ensure visibility
  // This provides better UX than blocking alert() and allows users to continue interacting
  const message = `We were unable to open your email client automatically.\n\nPlease contact our support team by emailing: ${SUPPORT_EMAIL}.\n\nIf your browser supports it, the support email address has been copied to your clipboard so you can paste it into your email application.`;

  toast.info(message, {
    autoClose: 10000, // 10 seconds - longer duration for important info
    position: 'top-center',
  });
};
